import type ImageType from "@travily/types/shared/Image";
import { Icon } from "@ui/components/Icon";
import { ResolvedImage } from "@ui/components/internal/ResolvedImage";
import { tv, type VariantProps } from "@ui/lib/tv";
import type { ReactNode } from "react";

const cardImage = tv({
  slots: {
    root: "relative aspect-square w-full overflow-hidden rounded-3xl bg-neutral-25",
    image: "h-full w-full object-cover",
    defaultFallback: "flex size-full items-center justify-center text-neutral-300",
    overlayTL: "absolute z-10",
    overlayTR: "absolute z-10",
    overlayBL: "absolute z-10",
    overlayBR: "absolute z-10",
  },
  defaultVariants: {
    overlayInset: "md",
  },
  variants: {
    overlayInset: {
      sm: {
        overlayTL: "left-4 top-4",
        overlayTR: "right-4 top-4",
        overlayBL: "bottom-4 left-4",
        overlayBR: "bottom-4 right-4",
      },
      md: {
        overlayTL: "left-6 top-6",
        overlayTR: "right-6 top-6",
        overlayBL: "bottom-6 left-6",
        overlayBR: "bottom-6 right-6",
      },
    },
  },
});

type CardImageVariants = VariantProps<typeof cardImage>;

export interface CardImageProps {
  /** Structured image (new path — uses `<Image>` with srcset, blurhash, lazy loading). */
  image?: ImageType | null;
  /** Legacy string URL. Prefer `image`. */
  src?: string;
  alt?: string;
  className?: string;
  /**
   * Rendered when there's no image. Defaults to a camera icon centered on the
   * neutral tint — avoids a bare gray card when a product has no photo yet.
   */
  fallback?: ReactNode;
  overlayBottomLeft?: ReactNode;
  overlayBottomRight?: ReactNode;
  overlayInset?: CardImageVariants["overlayInset"];
  overlayTopLeft?: ReactNode;
  overlayTopRight?: ReactNode;
  sizes?: string;
}

export function CardImage({
  image,
  src,
  alt,
  className,
  fallback,
  overlayBottomLeft,
  overlayBottomRight,
  overlayInset,
  overlayTopLeft,
  overlayTopRight,
  sizes = "(min-width: 768px) 33vw, 50vw",
}: CardImageProps) {
  const styles = cardImage({ overlayInset });
  const resolvedFallback = fallback ?? (
    <div className={styles.defaultFallback()} aria-hidden="true">
      <Icon name="camera" size="32" />
    </div>
  );

  return (
    <div className={styles.root({ className })}>
      <ResolvedImage
        image={image}
        src={src}
        alt={alt}
        sizes={sizes}
        className={styles.image()}
        fallback={resolvedFallback}
      />
      {overlayTopLeft && <div className={styles.overlayTL()}>{overlayTopLeft}</div>}
      {overlayTopRight && <div className={styles.overlayTR()}>{overlayTopRight}</div>}
      {overlayBottomLeft && <div className={styles.overlayBL()}>{overlayBottomLeft}</div>}
      {overlayBottomRight && <div className={styles.overlayBR()}>{overlayBottomRight}</div>}
    </div>
  );
}
